import axios from 'axios'
import toast from 'react-hot-toast'
import { triggerLimitExceeded } from '../hooks/useLimitHandler'
import logger from './logger'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? "",
  timeout: 30000,
})

/**
 * Pre-flight guard: проверяет что request body можно сериализовать.
 *
 * Раньше при ошибке `onClick={handler}` (где handler имеет default параметр)
 * React передавал SyntheticEvent в body. axios падал в JSON.stringify с
 * криптическим "Converting circular structure to JSON ... HTMLButtonElement
 * ... __reactFiber ... stateNode closes the circle", request не уходил,
 * юзер видел generic toast без понимания почему. Этот guard ловит такое
 * раньше и throw'ит понятную ошибку.
 *
 * Только для POST/PUT/PATCH (где body есть). GET/DELETE пропускаем.
 */
function assertSerializableBody(config) {
  if (!config.data) return
  const method = (config.method || '').toUpperCase()
  if (method !== 'POST' && method !== 'PUT' && method !== 'PATCH') return
  // Сам body — Blob/FormData/string/etc — axios сам разрулит, не трогаем
  if (typeof config.data !== 'object' || config.data instanceof FormData ||
      config.data instanceof Blob || config.data instanceof ArrayBuffer) return
  try {
    JSON.stringify(config.data)
  } catch (err) {
    if (err instanceof TypeError && /circular/i.test(err.message)) {
      // Найдём какие именно ключи проблемные — поможет дебажить причину
      const taintedKeys = []
      for (const [k, v] of Object.entries(config.data)) {
        try { JSON.stringify(v) } catch { taintedKeys.push(k) }
      }
      const url = config.url || '(no url)'
      const keysStr = taintedKeys.length ? ` Tainted keys: [${taintedKeys.join(', ')}]` : ''
      throw new Error(
        `Request body contains circular reference (likely SyntheticEvent or DOM node). ` +
        `Check that onClick handlers are wrapped in arrow functions. URL: ${url}.${keysStr}`
      )
    }
    throw err
  }
}

api.interceptors.request.use(
  (config) => {
    config.metadata = { start: new Date() }
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    assertSerializableBody(config)
    logger.debug('[API] Request:', config.method?.toUpperCase(), config.baseURL + config.url, { params: config.params, data: config.data })
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

api.interceptors.response.use(
  (response) => {
    const duration = new Date() - (response.config.metadata?.start || new Date())
    logger.debug('[API] Response:', response.config.method?.toUpperCase(), response.config.baseURL + response.config.url, response.status, `${duration}ms`)

    // Capture X-Trial-Warning header from successful responses (set by trialUsageCheck middleware at 80% cap)
    const trialWarning = response?.headers?.['x-trial-warning']
    if (trialWarning && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('trial-usage-warning', {
        detail: { message: trialWarning }
      }))
    }

    return response
  },
  (error) => {
    // Skip logging for cancelled/aborted requests (component unmount)
    if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError' || error.name === 'AbortError') {
      return Promise.reject(error)
    }

    const cfg = error.config || {}
    const duration = new Date() - (cfg.metadata?.start || new Date())
    logger.error('[API] Error:', cfg.method?.toUpperCase(), (cfg.baseURL||'') + (cfg.url||''), error.code || error.message, `${duration}ms`, error.response?.status, error.response?.data)

    // Capture X-Trial-Warning header from error responses too (may still be set if middleware ran before failure)
    const trialWarningErr = error?.response?.headers?.['x-trial-warning']
    if (trialWarningErr && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('trial-usage-warning', {
        detail: { message: trialWarningErr }
      }))
    }

    // Handle 423 LOCKED — account locked after trial expiry without payment.
    // RecoverySubscriptionModal listens for this and shows the recovery UI; do not redirect.
    if (error.response?.status === 423 && error.response.data?.code === 'ACCOUNT_LOCKED') {
      if (typeof window !== 'undefined') {
        // 1) Open the recovery plan picker.
        window.dispatchEvent(new CustomEvent('account-locked', {
          detail: { lockedAt: error.response.data.lockedAt }
        }))
        // 2) Sync trialStore so the dashboard hero/dim/useTrialGate stop showing
        //    the stale 'active' state — otherwise the user keeps firing writes that 423.
        //    Lazy-import to avoid an import cycle: trialStore imports this api module
        //    at the top level, so importing it here at module scope would create a
        //    circular dependency (api widely imported -> partially-initialized binding).
        //    fetchLockState() also refreshes recoveryDiscountPercent for the picker.
        import('../store/trialStore')
          .then(({ useTrialStore }) => {
            const store = useTrialStore.getState()
            store.fetchLockState().catch(() => {
              // Fallback: at minimum reflect the locked state from the 423 payload
              // so the UI gates immediately even if the refresh request fails.
              useTrialStore.setState({
                lockState: 'view_only',
                inTrial: false,
                lockedAt: error.response.data.lockedAt ?? store.lockedAt,
              })
            })
          })
          .catch(() => { /* store unavailable — event dispatch already fired */ })
      }
    }

    // Handle 410 GONE — account soft-deleted after prolonged inactivity in
    // view_only state. Recovery is still possible by paying, so open the same
    // plan picker as 423. Without this branch the write fails silently (no
    // toast, no redirect) and the user sees nothing happen.
    if (error.response?.status === 410 && error.response.data?.code === 'ACCOUNT_DELETED') {
      if (typeof window !== 'undefined') {
        toast.error('Аккаунт деактивирован после долгого простоя. Оформи подписку, чтобы вернуть доступ.')
        window.dispatchEvent(new CustomEvent('account-locked', {
          detail: { soft_deleted: true }
        }))
        import('../store/trialStore')
          .then(({ useTrialStore }) => {
            useTrialStore.getState().fetchLockState().catch(() => {
              useTrialStore.setState({ lockState: 'soft_deleted', inTrial: false })
            })
          })
          .catch(() => { /* store unavailable — event dispatch already fired */ })
      }
    }

    if (error.response?.status === 401) {
      localStorage.removeItem('token')
      delete api.defaults.headers.common['Authorization']
      // Не редиректим если уже на странице логина (чтобы показать toast вместо перезагрузки)
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login'
      }
    }

    // Handle validation errors (400) - show specific error message
    if (error.response?.status === 400) {
      const data = error.response.data
      let errorMessage = data?.message || 'Ошибка валидации'

      // If there are specific validation errors, show the first one
      if (data?.errors?.length > 0) {
        const firstError = data.errors[0]
        errorMessage = firstError.msg || firstError.message || errorMessage
      }

      toast.error(errorMessage)
    }

    // Handle limit exceeded errors
    if (error.response?.status === 403) {
      if (error.response?.data?.code === 'LIMIT_EXCEEDED') {
        const limitType = error.response.data.message?.includes('video') ? 'video' : 'ai';
        triggerLimitExceeded(limitType);
      } else {
        // Show 403 error message (e.g., "Insufficient video limit")
        const message = error.response.data?.message || error.response.data?.error || 'Доступ запрещён'
        toast.error(message)
      }
    }

    // Handle server errors (500+) - show message from backend (user-friendly)
    if (error.response?.status >= 500) {
      const message = error.response.data?.message || 'Ошибка сервера. Попробуйте позже.'
      toast.error(message)
    }

    // Handle 503 Service Unavailable (external services down)
    if (error.response?.status === 503) {
      const message = error.response.data?.message || 'Сервис временно недоступен. Попробуйте позже.'
      toast.error(message)
    }

    return Promise.reject(error)
  }
)

export default api